// docs/54: the connection's notices over every screen. A drop first shows the reconnecting strip
// (RC1) and leaves the player where they are; past the grace window the "connection lost" card
// blocks the screen with Reconnect now (RC3); a drop that lasted comes back with one toast (RC4).
// The boot path, where no connection was ever made, is `BootOffline` (RC6).
import { useEffect, useRef, useState } from 'react';
import { Banner, Button, Display, Eyebrow, Modal, Panel, Spinner } from '../components';
import { QUIET_BLIP_MS, useLink } from '../../core/stores/link';
import { reconnectNow, STDB_DB, STDB_URI } from '../../core/net/connection';
import { useToasts } from '../../core/net/reducers';
import { useSession } from '../../core/stores/session';
import { deviceNow } from '../../core/time';

/** A device-clock tick for the retry countdown. */
function useTick(on: boolean, every = 500): number {
  const [now, setNow] = useState(() => deviceNow());
  useEffect(() => {
    if (!on) return;
    const t = window.setInterval(() => setNow(deviceNow()), every);
    return () => window.clearInterval(t);
  }, [on, every]);
  return now;
}

function retryLine(attempt: number, nextAttemptAt: number | undefined, now: number): string {
  if (nextAttemptAt === undefined) return attempt > 1 ? `Trying again (attempt ${attempt})…` : 'Trying again…';
  const s = Math.ceil((nextAttemptAt - now) / 1000);
  return s > 0 ? `Next try in ${s} s (attempt ${attempt})` : `Trying now (attempt ${attempt})…`;
}

/** "Reconnect now" with a short busy state, so a press always shows that something happened. */
function ReconnectButton({ testId, label = 'Reconnect now', ...rest }: { testId: string; label?: string; primary?: boolean; size?: 'lg' }) {
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!busy) return; const t = setTimeout(() => setBusy(false), 1500); return () => clearTimeout(t); }, [busy]);
  return <Button {...rest} loading={busy} data-autofocus onClick={() => { setBusy(true); reconnectNow(); }} data-testid={testId}>{label}</Button>;
}

export function LinkStrip() {
  const state = useLink((s) => s.state);
  const device = useLink((s) => s.deviceOffline);
  if (state !== 'reconnecting' && !(device && state === 'online')) return null;
  return (
    <Banner tone="warn" className="fk-link-strip" role="status" aria-live="polite" data-testid="link-reconnecting">
      <Spinner label="Reconnecting" />
      <span>{device ? 'Your device is offline. Waiting for the network…' : 'Connection lost. Reconnecting…'}</span>
      <span className="fk-link-strip__sub">· nothing is sent until it is back</span>
    </Banner>
  );
}

export function LinkLost() {
  const state = useLink((s) => s.state);
  const attempt = useLink((s) => s.attempt);
  const nextAttemptAt = useLink((s) => s.nextAttemptAt);
  const device = useLink((s) => s.deviceOffline);
  const since = useLink((s) => s.since);
  const lost = state === 'lost';
  const now = useTick(lost);
  const away = since !== undefined ? Math.max(0, Math.round((now - since) / 1000)) : 0;
  return (
    <Modal open={lost} onClose={() => {}} dismissible={false} size="narrow" eyebrow="Connection lost" title="The World is out of reach" testId="link-lost"
      footer={<>
        <Button onClick={() => location.reload()} data-testid="link-reload">Reload the game</Button>
        <ReconnectButton primary testId="link-reconnect" />
      </>}>
      <p style={{ margin: 0 }}>
        {device ? 'This device has no network.' : 'The connection to the World dropped and has not come back yet.'} Your fellowship is safe: everything lives on the
        server, and you pick up where you left off when you are back.
      </p>
      <p className="fk-muted" style={{ margin: 0 }}>A turn that closes while you are away goes on without you, and your heroes Hold.</p>
      <p className="fk-link-lost__status" data-testid="link-lost-status"><Spinner label="Reconnecting" /> {device ? 'Waiting for the network…' : retryLine(attempt, nextAttemptAt, now)} · away {away} s</p>
    </Modal>
  );
}

/** RC4: one toast when a drop that lasted comes back (a blip stays quiet). */
function useBackToast() {
  const state = useLink((s) => s.state);
  const since = useLink((s) => s.since);
  const droppedAt = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (state !== 'online') { droppedAt.current ??= since; return; }
    const at = droppedAt.current;
    droppedAt.current = undefined;
    if (at !== undefined && deviceNow() - at >= QUIET_BLIP_MS) useToasts.getState().push('Back in the World. Everything is caught up.', 'success');
  }, [state, since]);
}

export function LinkNotices() {
  useBackToast();
  return <><LinkStrip /><LinkLost /></>;
}

/** RC6: the first connection failed. The Provider's pool keeps retrying; Try now does it at once. */
export function BootOffline() {
  const error = useSession((s) => s.error);
  const attempt = useLink((s) => s.attempt);
  const device = useLink((s) => s.deviceOffline);
  return (
    <div className="fk-center"><Panel variant="ornate" label="Offline" className="fk-hit fk-offline" data-testid="offline">
      <Eyebrow>Offline</Eyebrow>
      <Display size="lg">The World is out of reach</Display>
      <p>{device ? 'This device has no network.' : <>Could not reach the server at <code>{STDB_URI}</code> / <code>{STDB_DB}</code>.</>} Fellowkeep is played online, so it waits here until it can connect.</p>
      <p className="fk-muted">{error}</p>
      <p className="fk-link-lost__status" data-testid="offline-status"><Spinner label="Retrying" /> Trying again on its own{attempt > 1 ? ` (attempt ${attempt})` : ''}…</p>
      <ReconnectButton primary size="lg" testId="retry" label="Try now" />
    </Panel></div>
  );
}
